import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { Minus, Plus, ShoppingCart, Heart, Share2, Truck, Shield, RotateCcw, ChevronRight, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageAt = (images, index) => {
  if (!images) return '';
  return images.length > index ? images[index] : '';
};

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const { addToCart, loading: cartLoading } = useCart();

  const fetchProduct = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleAddToCart = async () => {
    try {
      await addToCart(product.id, quantity);
      toast.success('Added to cart', { description: `${quantity}x ${product.name} added to your cart.` });
    } catch (error) {
      toast.error('Error adding to cart');
    }
  };

  if (loading) {
    return <Layout><div className="flex justify-center items-center min-h-[60vh]"><div className="spinner" /></div></Layout>;
  }

  if (!product) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h1 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-4">Product Not Found</h1>
          <p className="text-[#6B7280] mb-6">The product you're looking for doesn't exist.</p>
          <Button asChild><Link to="/shop">Back to Shop</Link></Button>
        </div>
      </Layout>
    );
  }

  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const currentImage = getImageAt(product.images, selectedImage);
  const features = product.features || [];
  const images = product.images || [];

  const renderThumbnail = (img, idx) => (
    <button key={idx} onClick={() => setSelectedImage(idx)} className={`gallery-thumb ${selectedImage === idx ? 'active' : ''}`} data-testid={`thumbnail-${idx}`}>
      <img src={img} alt={`${product.name} view ${idx + 1}`} className="w-full h-full object-cover" />
    </button>
  );

  const renderFeature = (f, i) => (
    <li key={i} className="flex items-center gap-2 text-[#4B5563]">
      <Check size={16} className="text-[#10B981]" />{f}
    </li>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <nav className="breadcrumb" data-testid="breadcrumb">
          <Link to="/">Home</Link><ChevronRight size={14} /><Link to="/shop">Shop</Link><ChevronRight size={14} /><span className="text-[#1A1A1A]">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20" data-testid="product-detail">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            <div className="gallery-main relative" data-testid="product-main-image">
              <img src={currentImage} alt={product.name} className="w-full h-full object-cover" />
              {hasDiscount && <span className="badge-sale">Sale</span>}
            </div>
            <div className="gallery-thumbnails">{images.map(renderThumbnail)}</div>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-[#8B5E3C] text-sm font-medium uppercase tracking-wider mb-2">{product.category}</p>
              <h1 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">{product.name}</h1>
              <div className="flex items-baseline gap-3">
                {hasDiscount ? (
                  <>
                    <span className="text-3xl font-bold text-[#EF4444]">${product.sale_price.toFixed(2)}</span>
                    <span className="text-xl text-[#9CA3AF] line-through">${product.price.toFixed(2)}</span>
                    <span className="bg-[#EF4444] text-white text-xs px-2 py-1 font-medium">SAVE ${(product.price - product.sale_price).toFixed(2)}</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-[#1A1A1A]">${product.price.toFixed(2)}</span>
                )}
              </div>
            </div>

            <p className="text-[#4B5563] leading-relaxed">{product.description}</p>

            <div className="grid grid-cols-2 gap-4 py-6 border-t border-b border-[#E5E7EB]">
              <div><p className="text-sm text-[#6B7280]">SKU</p><p className="font-medium">{product.sku}</p></div>
              <div><p className="text-sm text-[#6B7280]">Style</p><p className="font-medium">{product.style}</p></div>
              <div><p className="text-sm text-[#6B7280]">Color</p><p className="font-medium">{product.color}</p></div>
              <div><p className="text-sm text-[#6B7280]">Finish</p><p className="font-medium">{product.finish}</p></div>
              <div className="col-span-2"><p className="text-sm text-[#6B7280]">Dimensions</p><p className="font-medium">{product.dimensions}</p></div>
              <div className="col-span-2"><p className="text-sm text-[#6B7280]">Materials</p><p className="font-medium">{product.materials}</p></div>
            </div>

            {features.length > 0 && (
              <div>
                <h3 className="font-manrope font-semibold text-[#1A1A1A] mb-3">Features</h3>
                <ul className="space-y-2">{features.map(renderFeature)}</ul>
              </div>
            )}

            <div className="space-y-4 pt-6">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">Quantity:</span>
                <div className="quantity-selector">
                  <button className="quantity-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))} data-testid="decrease-quantity"><Minus size={16} /></button>
                  <input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="quantity-input" data-testid="quantity-input" />
                  <button className="quantity-btn" onClick={() => setQuantity(q => q + 1)} data-testid="increase-quantity"><Plus size={16} /></button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="flex-1 bg-[#1A1A1A] hover:bg-[#333333]" onClick={handleAddToCart} disabled={cartLoading || !product.in_stock} data-testid="add-to-cart-btn">
                  <ShoppingCart className="mr-2" size={20} />{product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                </Button>
                <Button size="lg" variant="outline" className="flex-1 border-[#8B5E3C] text-[#8B5E3C] hover:bg-[#8B5E3C] hover:text-white" asChild>
                  <Link to="/quote" data-testid="request-quote-btn">Request Quote</Link>
                </Button>
              </div>

              <div className="flex gap-4">
                <button className="flex items-center gap-2 text-[#6B7280] hover:text-[#8B5E3C]"><Heart size={18} /><span className="text-sm">Add to Wishlist</span></button>
                <button className="flex items-center gap-2 text-[#6B7280] hover:text-[#8B5E3C]"><Share2 size={18} /><span className="text-sm">Share</span></button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-[#E5E7EB]">
              <div className="flex items-center gap-3"><Truck size={24} className="text-[#8B5E3C]" /><div><p className="font-medium text-sm">Free Shipping</p><p className="text-xs text-[#6B7280]">Orders over $2,500</p></div></div>
              <div className="flex items-center gap-3"><Shield size={24} className="text-[#8B5E3C]" /><div><p className="font-medium text-sm">5-Year Warranty</p><p className="text-xs text-[#6B7280]">Full coverage</p></div></div>
              <div className="flex items-center gap-3"><RotateCcw size={24} className="text-[#8B5E3C]" /><div><p className="font-medium text-sm">Easy Returns</p><p className="text-xs text-[#6B7280]">30-day policy</p></div></div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
